import type { DragEndEvent } from "@dnd-kit/core";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { t } from "@lingui/macro";
import { Plus } from "@phosphor-icons/react";
import type { SectionItem, SectionKey, SectionWithItem } from "@reactive-resume/schema";
import {
  Button,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";
import get from "lodash.get";
import { useState } from "react";

import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import { SectionListItem } from "./section-list-item";
import { SectionOptions } from "./section-options";
import { SectionHeader } from "@/client/pages/builder/_components/SectionHeader";

type Props<T extends SectionItem> = {
  id: SectionKey;
  openedDataSection: string | undefined;
  setOpenedDataSection: (a: string) => void;
  title: (item: T) => string;
  description?: (item: T) => string | undefined;
};

export const SectionBase = <T extends SectionItem>({
  id,
  title,
  openedDataSection,
  setOpenedDataSection,
  description,
}: Props<T>) => {
  const { open } = useDialog(id);

  const setValue = useResumeStore((state) => state.setValue);
  const section = useResumeStore((state) =>
    get(state.resume.data.sections, id),
  ) as SectionWithItem<T>;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!section) return null;

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = section.items.findIndex((item) => item.id === active.id);
      const newIndex = section.items.findIndex((item) => item.id === over.id);

      const sortedList = arrayMove(section.items as T[], oldIndex, newIndex);
      setValue(`sections.${id}.items`, sortedList);
    }
  };

  const onCreate = () => {
    open("create", { id });
  };

  const onUpdate = (item: T) => {
    open("update", { id, item });
  };

  const onDuplicate = (item: T) => {
    open("duplicate", { id, item });
  };

  const onDelete = (item: T) => {
    open("delete", { id, item });
  };

  const onToggleVisibility = (index: number) => {
    const visible = get(section, `items[${index}].visible`, true);
    setValue(`sections.${id}.items[${index}].visible`, !visible);
  };

  return (
    <motion.section
      id={id}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="grid gap-y-4"
    >
      <Accordion
        type="single"
        collapsible
        value={openedDataSection}
        onValueChange={setOpenedDataSection}
      >
        <AccordionItem value={id} className="border-none">
          <div className="hover:decoration-none! relative flex w-full items-center justify-between [&>h3]:w-full [&>h3]:cursor-pointer">
            <AccordionTrigger className="w-full flex-1 rounded-sm px-2 py-1">
              <SectionHeader icon={id} title={section.name} />
            </AccordionTrigger>
            {openedDataSection === id && (
              <div className="absolute top-1 right-5 flex items-center gap-x-2">
                <SectionOptions id={id} />
              </div>
            )}
          </div>

          <AccordionContent>
            <main className={cn("mt-4 grid transition-opacity", !section.visible && "opacity-50")}>
              {section.items.length === 0 && (
                <Button
                  variant="outline"
                  className="hover:bg-secondary-accent gap-x-2 border-dashed py-6 leading-normal"
                  onClick={onCreate}
                >
                  <Plus size={14} />
                  <span className="font-medium">
                    {t({
                      message: "Add a new item",
                      context: "For example, add a new work experience, or add a new profile.",
                    })}
                  </span>
                </Button>
              )}

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToParentElement]}
                onDragEnd={onDragEnd}
              >
                <SortableContext items={section.items} strategy={verticalListSortingStrategy}>
                  <AnimatePresence>
                    {section.items.map((item, index) => (
                      <SectionListItem
                        key={item.id}
                        id={item.id}
                        visible={item.visible}
                        title={title(item as T)}
                        description={description?.(item as T)}
                        onUpdate={() => {
                          onUpdate(item as T);
                        }}
                        onDelete={() => {
                          onDelete(item as T);
                        }}
                        onDuplicate={() => {
                          onDuplicate(item as T);
                        }}
                        onToggleVisibility={() => {
                          onToggleVisibility(index);
                        }}
                      />
                    ))}
                  </AnimatePresence>
                </SortableContext>
              </DndContext>
            </main>
            {section.items.length > 0 && (
              <footer className="mt-4 flex items-center justify-end">
                <Button
                  variant="outline"
                  className="ml-auto gap-x-2 text-xs lg:text-sm"
                  onClick={onCreate}
                >
                  <Plus />
                  <span>
                    {t({
                      message: "Add a new item",
                      context: "For example, add a new work experience, or add a new profile.",
                    })}
                  </span>
                </Button>
              </footer>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </motion.section>
  );
};
